/**
 * 
 * @FileName : CodePage
 * @Description : This is system common code management page
 * 
 * @author minjlee
 * @since 2023. 03.10
 * @version 0.1
 *
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from 'react-router-dom';
import { useTranslation } from "react-i18next";
//MUI
import { Box, Divider, Stack, Button, Checkbox, } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import DownloadIcon from '@mui/icons-material/Download';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

//API
import AuthenticationService from "../../pages/auth/AuthenticationService";
import { fnExcel, fni18n } from "../../services/commonService";
import { API } from "../../config";
import { getAPI, postAPI, putAPI, deleteAPI } from "../../services/commonService";

// custom UI
import UTable from "../../components/mui/UTable";
import UDataGrid from "../../components/mui/UDataGrid";
import UBreadCrumbs from "../../components/mui/UBreadCrumbs";
import UTextField from "../../components/mui/UTextField";
import UButton from "../../components/mui/UButton";
import UAuthButton from "../../components/mui/UAuthButton";
import UPagination from "../../components/mui/UPagination";
import CodeDetailPage from "../system/CodeDetailPage";
import UConfirmDialog from "../../components/mui/UConfirmDialog";
import UAlertDialog from "../../components/mui/UAlertDialog";
import USnackbar from "../../components/mui/USnackbar";
import CodeForm from "./CodeForm";
import UAuthFabAdd from "../../components/mui/UAuthFabAdd";
import USelectBox from "../../components/mui/USelectBox";
import { theme } from '../../assets/themes';
import UPopupForm from "../../components/mui/UPopupForm";

const useYn = [{cd:'Y', name:'Y'}, {cd:'N', name:'N'}, ];

const CodePage = () => {
    const { t } = useTranslation();
    const userLoginId = AuthenticationService.getLoggedInUserID();
    const [ rowSelected, setRowSelected ] = useState([]); // 선택 리스트
    const [ codeList, setCodeList ] = useState([]); // 리스트
    const [ codeDetailInfo, setCodeDetailInfo ] = useState({
      useYn: 'Y',
      regEmpNo: userLoginId,
    }); // 상세
    const [ loading, setLoading ] = useState(false); // 조회 리스트 로딩
    const [ searchVal, setSearchVal ] = useState({
      useYn: 'Y',
    }); // 검색어

    const [ columnVisibilityModel, ] = useState({
      regEmpNo: false,
      remk: false,
    }); // hidden colum

    const [ confirmPopupOpen, setConfirmPopupOpen ] = useState(false); // confirm dialog
    const [ errorPopupOpen, setErrorPopupOpen ] = useState(false); // error dialog
    const [ showError, setShowError ] = useState(false);
    const [ errorMsg, setErrorMsg ] = useState(""); // api error message
    const [ snackOpen, setSnackOpen ] = useState(false);
    const [ snackSeverity, ] = useState('success');
    const [ snackMessage, setSnackMessage ] = useState('');
    const [ formOpen, setFormOpen ] = useState(false); // modify dialog

    const location = useLocation();

    //화면 로드 시
    useEffect(() => {
      handleSearch();
    }, [])

    //검색 조건 setter
    const handleChange = (e) => {
      const { name, value } = e.target;
      setSearchVal({
        ...searchVal,
        [name] : value
      })
    }

    //입력 데이타 유효성 검사
    const errors = useMemo(() => {
      const errors = {};
      if ( !codeDetailInfo.cdGubn ) {
        errors.cdGubn = fni18n('필수 입력 항목입니다')//'코드구분은 필수 입력 항목입니다.'
      } 
      if ( !codeDetailInfo.cd ) {
        errors.cd = fni18n('필수 입력 항목입니다')//'코드번호는 필수 입력 항목입니다.'
      }
      if ( !codeDetailInfo.cdNm ) {
        errors.cdNm = fni18n('필수 입력 항목입니다')//'코드명은 필수 입력 항목입니다.'
      }
      if ( codeDetailInfo.cdGubn && codeDetailInfo.cdGubn.length > 3 ) {
        errors.cdGubn = fni18n('최대 길이가 3입니다')//'코드구분은 최대 길이가 3입니다.'
      }
      if ( codeDetailInfo.cd && codeDetailInfo.cd.length > 60 ) {
        errors.cd = fni18n('최대 길이가 60입니다')//'코드번호는 최대 길이가 60입니다.'
      }
      if ( codeDetailInfo.cdNm && codeDetailInfo.cdNm.length > 60 ) {
        errors.cdNm = fni18n('최대 길이가 60입니다')//'코드명은 최대 길이가 60입니다.'
      }
      if ( codeDetailInfo.cdShortNm && codeDetailInfo.cdShortNm.length > 60 ) {
        errors.cdShortNm = fni18n('최대 길이가 60입니다')//코드약어는 최대 길이가 60입니다.'
      }
      if ( codeDetailInfo.cdLen && codeDetailInfo.cdLen.length > 2 ) {
        errors.cdLen = fni18n('최대 길이가 2입니다')//'코드길이는 최대 길이가 2입니다.'
      }
      if ( codeDetailInfo.remk && codeDetailInfo.remk.length > 100 ) {
        errors.remk = fni18n('최대 길이가 100입니다')//'설명은 최대 길이가 100입니다.'
      }
      return errors;
      }, [codeDetailInfo])

    // 검색 함수
    const handleSearch = async () => {
      try {
        setLoading(true);
        await getAPI(API.SYSTEM.CODE, searchVal)
        .then((res) => {
          setCodeList(res.data.result)
        })
        .catch((err) => {
          console.log(err)
        })
      } catch (error) {
        console.log(error)
      }
      setLoading(false);
    }

    // 상세 정보 변경 시 setter
    const onDetailChange = (e) => {
      const { name, value } = e.target;
      setRowSelected({
        ...rowSelected,
        [name] : value,
      });
      setCodeDetailInfo({
        ...codeDetailInfo,
        [name] : value,
      });
    };

    // 상세 YN 변경 시 setter
    const onSwitchChange = (e) => {
      setCodeDetailInfo({
        ...codeDetailInfo,
        [e.target.name]: e.target.checked === true ? "Y" : "N",
      });
    };

    //DataGrid에서 row 선택 시 
    const onSelectedRow = (row) => {
      setRowSelected(row)
      setCodeDetailInfo(row)
      handleFormOpen()
    };

    // 저장함수
    const handleSave = () => {
      if ( Object.keys(errors).length > 0) {
        // handleErrorOpen()
        setShowError(true)
        return;
      }
      try {
        postAPI(API.SYSTEM.CODE, codeDetailInfo)
        .then((res) => {
          if (res.data.code === 200) {
            //handleCancel();
            handleSearch();
            // snack bar success open
            setSnackMessage(res.data.message);
            handleClick();
            handleFormClose()
          } else {
            // setErrorMsg(res.data.message || "Processing failed.")
            setErrorMsg(t("처리가 실패하였습니다"))
            handleErrorOpen()
          }
        })
        .catch((err) => {
          console.log('>>', err)
        })
      } catch (error) {
        console.log('>>>', error)
      }
    }

    // 삭제함수
    const handleDelete = () => {
      // if ( Object.keys(errors).length > 0 || codeDetailInfo.useYn === 'N') {
      //   return;
      // }
      try {
        deleteAPI(API.SYSTEM.CODE, {cdGubn: codeDetailInfo.cdGubn, cd: codeDetailInfo.cd})
        .then((res) => {
          if(res.data.code === 200) {
            // handleCancel();
            handleSearch();
            // snack bar success open
            setSnackMessage(res.data.message);
            handleClick();
            handleFormClose()
          } else {
            // setErrorMsg(res.data.message || "Processing failed.")
            setErrorMsg(t("처리가 실패하였습니다"))
            handleErrorOpen()
          }
        })
      } catch (error) {
        console.log(error)
      } finally{
        handleConfirmClose()
      }
    }

    // 되돌리기 함수
    const handleCancel = () => {
      setRowSelected({})
      setCodeDetailInfo({
        useYn: "Y",
        regEmpNo: userLoginId,
      })
      setShowError(false)
    }

    //삭제 확인 팝업 열기 함수
    const handleConfirmOpen = () => {
      // if ( Object.keys(errors).length > 0  || codeDetailInfo.useYn === 'N') {
      //   return;
      // }
      setConfirmPopupOpen(true);
    };
    //삭제 확인 팝업 닫기 함수
    const handleConfirmClose = () => {
      setConfirmPopupOpen(false);
    };

    //오류팝업 열기 함수
    const handleErrorOpen = () => {
      setErrorPopupOpen(true);
    };
    //오류팝업 닫기 함수
    const handleErrorClose = () => {
      setErrorMsg("")
      setErrorPopupOpen(false);
    };
    //성공 스낵바 열기 함수
    const handleClick = () => {
      setSnackOpen(true);
    };
    //성공 스낵바 닫기 함수
    const handleClose = (event, reason) => {
      if (reason === 'clickaway') {
        return;
      }
      setSnackOpen(false);
    };
    //신규등록
    const handleAddNew = () => {
      handleCancel()
      handleFormOpen()
    }
    //폼 팝업
    const handleFormOpen = () => {
      setFormOpen(true);
    };
    const handleFormClose = () => {
      setFormOpen(false);
    };
  //main list column set
  const columns = [
    /*
    {
      field: "id",
      headerName: "번호",
      width: 70,
      headerAlign: "center",
      align: "center",
    },
    */
    {
      field: "cdGubn",
      headerName: "코드구분",
      width: 100,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "cd",
      headerName: "코드번호",
      width: 100,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "cdNm",
      headerName: "코드명",
      width: 200,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "cdShortNm",
      headerName: "코드약어",
      width: 200,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "useYn",
      headerName: "사용여부",
      width: 100,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params)=>{
        if (params.value == 'Y') {
          return <Checkbox checked={true} disabled style={{color: theme.palette.primary.main}} />
        } else {
          return <Checkbox checked={false} disabled/>
        }
      },
    },
    {
      field: "cdLen",
      headerName: "코드길이",
      width: 100,
      headerAlign: "center",
      align: "center",
      },
    {
      field: "regEmpNm",
      headerName: "등록자",
      width: 150,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "regDt",
      headerName: "등록일자",
      width: 150,
      headerAlign: "center",
      align: "center",
      /*valueFormatter: (params) => {
        if (params.value == null) {
          return "";
        }
        const valueFormatted = params.value.replace(
          /(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/g,
          "$1-$2-$3"
        );
        return `${valueFormatted}`;
      },*/
    },
    /*
    {
      field: "regEmpNo",
      headerName: "등록자ID",
      width: 150,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "remk",
      headerName: "설명",
      width: 200,
      headerAlign: "center",
      align: "center",
    },
    */
    {
      field: 'modify',
      headerName: '설정',
      width: 100,
      headerAlign: "center",
      align: "center",
      renderCell: (params) =>
      (
        <UButton
        variant="outlined"
        // onButtonClick={handleFormOpen}
        onButtonClick={()=>onSelectedRow(params.rows)}
        menuFrom={location.pathname}
        >
        보기
        </UButton>
      ),
    },
  ];

  return (
    <div className="main-container">
      <div className="header">
        <Grid container>
          <Grid item xs={10}>
            {/* 상단메뉴명 */}
            <UBreadCrumbs
                title={"시스템관리"}
                subtitle={"공통코드관리"}
                />
          </Grid>
          <Grid item xs={2}>
            {/* 툴바 버튼 */}
            <Stack 
              direction="row"
              justifyContent="flex-end"
              alignItems="flex-start"
              spacing={0.5}
            >
              {/* <UAuthButton icon="Add" menuFrom={location.pathname} onButtonClick={handleAddNew}>추가</UAuthButton>
              <UButton icon="Search" onButtonClick={handleSearch}>조회</UButton> */}
            </Stack>
          </Grid>
        </Grid>
      </div>
        {/* <Divider/>  */}
        
        <Box className="contents">
        {/* 조회조건 */}
          <TableContainer className="table-container" component={Paper} elevation={0}>
          <Table className="table" size="small">
              <TableBody>
              <TableRow className="table-row-header">
                  <TableCell className="table-cell-title">{fni18n('코드구분')}</TableCell>
                  <TableCell className="table-cell">
                    <UTextField
                    name="cdGubn"
                    onChange={handleChange} 
                    /> 
                  </TableCell>
                  <TableCell className="table-cell-title">{fni18n('코드번호')}</TableCell>
                  <TableCell className="table-cell">
                    <UTextField
                    name="cd"
                    onChange={handleChange} 
                    /> 
                  </TableCell>
                  <TableCell className="table-cell-title">{fni18n('코드명')}</TableCell>
                  <TableCell className="table-cell">
                    <UTextField
                    name="cdNm"
                    onChange={handleChange} 
                    /> 
                  </TableCell>
                  <TableCell className="table-cell-title">{fni18n('사용여부')}</TableCell>
                  <TableCell className="table-cell">
                    <USelectBox
                      id="useYn"
                      name="useYn"
                      items={useYn}
                      onItemChange={handleChange}
                      defaultValue={searchVal.useYn}
                    />
                  </TableCell>
              </TableRow>
              </TableBody>
          </Table>
          </TableContainer>
          <div className="search">
              <UButton icon="Search" onButtonClick={handleSearch}>조회</UButton>
          </div>
          {/* <Divider/> */}

          {/* 조회 리스트 */}
          <div style={{  width: "100%", display: "grid" }}>
            {/* <UDataGrid columns={columns} data={codeList} onSelectedRow={onSelectedRow} /> */}
            <UTable columns={columns} rows={codeList} rowPerPage={11}
            toolbarAdd={
              <UAuthButton icon="Add" menuFrom={location.pathname} onButtonClick={handleAddNew}>추가</UAuthButton>
            }  
            />
          </div>
        </Box> 
        {/* 상세 처리 버튼 리스트 */}
        {/* 조회 리스트 선택 상세  */}
        {/*
        <Box sx={{ p: 1 }}>
        <Grid container>
          <Grid item xs={9}>
          </Grid>
          <Grid item xs={3}>
          <Stack direction="row" spacing={2}>
            <FormControl sx={{ width: btnSize_wid }}>
              <UButton
                icon="Save"
                onButtonClick={handleSave}
              >
                저장
              </UButton>
            </FormControl>
            <FormControl sx={{ width: btnSize_wid }}>
              <UButton
                icon="Delete"
                onButtonClick={handleConfirmOpen}
              >
                삭제
              </UButton>
            </FormControl>
            <FormControl sx={{ width: btnSize_wid }}>
              <UButton
                icon="Cancel"
                onButtonClick={handleCancel}
              >
                취소
              </UButton>
            </FormControl>
            </Stack>
          </Grid>
        </Grid>
        </Box>

        <CodeDetailPage 
        data={codeDetailInfo} 
        onChange={onDetailChange} 
        onSwitchChange={onSwitchChange}
        errors={errors}
        showError={showError}
        />
*/}
        {/* 삭제 확인 팝업  */}
        <UConfirmDialog
        message={'[' + codeDetailInfo.cdGubn + '-' + codeDetailInfo.cd + ']' + codeDetailInfo.cdNm + ' Are you sure you want to delete?'}//코드를 삭제하시겠습니까?
        open={confirmPopupOpen}
        onClose={handleConfirmClose}
        onClickOk={handleDelete}
        />

        {/* 오류 확인 팝업  */}
        <UAlertDialog
        alerts={errors}
        message={errorMsg}
        open={errorPopupOpen}
        onClose={handleErrorClose}
        />

        {/* 성공 스낵바  */}
        {/* <USnackbar
        open={snackOpen}
        onClose={handleClose}
        severity={snackSeverity}
        message={snackMessage}
        /> */}

        {/* 폼 팝업  */}
        {/* <CodeForm
        title={fni18n("공통코드관리")}
        open={formOpen}
        onClose={handleFormClose}
        onSave={handleSave}
        onDelete={handleConfirmOpen}
        detailInfo={codeDetailInfo} 
        onDetailChange={onDetailChange} 
        onSwitchChange={onSwitchChange}
        errors={errors}
        showError={showError}
        /> */}
        <UPopupForm
          open={formOpen}
          onClose={handleFormClose}
          onSave={handleSave}
          title="공통코드 설정"
          type={"2"}
          content={
            <CodeDetailPage 
            data={codeDetailInfo} 
            onChange={onDetailChange} 
            onSwitchChange={onSwitchChange}
            errors={errors}
            showError={showError}
            />
          }
        /> 

        {/* 추가버튼 */}
        {/* <UAuthFabAdd menuFrom={location.pathname} onClick={handleAddNew} /> */}
    </div>

  );
}
export default CodePage;